import { createPortal } from 'react-dom'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/base/buttons'
import { CheckCircleIcon, AlertCircleIcon, XCloseIcon, HelpCircleIcon, ArrowUpRightIcon } from '@untitledui-icons/react/line'
import type { BookingSystem } from '@/types/onboarding'

// Helper component for external links in tutorials
function TutorialLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#6941c6] hover:underline font-medium"
    >
      {children}
    </a>
  )
}

interface BaseModalProps {
  isOpen: boolean
  onClose: () => void
}

// Base modal wrapper
function ModalWrapper({ isOpen, onClose, children }: BaseModalProps & { children: React.ReactNode }) {
  if (!isOpen) return null
  
  return createPortal(
    <div className="onboarding-layout fixed inset-0 z-50 flex items-center justify-center p-4" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-[rgba(10,13,18,0.64)]" 
        style={{ opacity: 0.7 }}
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative w-full max-w-[400px] rounded-2xl bg-white shadow-xl">
        {children}
      </div>
    </div>,
    document.body
  )
}

// Loading Modal - Checking integration
export function BookingValidationLoadingModal({ isOpen, onClose }: BaseModalProps) {
  const { t } = useTranslation()
  return (
    <ModalWrapper isOpen={isOpen} onClose={() => {}}>
      <div className="relative flex flex-col w-full">
        <div className="flex flex-col gap-4 items-start pt-6 px-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f5f5f5]">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#b0b0b0] border-t-[#535862]" />
          </div>
          <div className="flex flex-col gap-0.5 w-full">
            <p className="text-xl font-semibold text-[#181d27] leading-[30px]">
              {t('resources.signup.booking_validation.loading_title', "We're checking the integration, please don't leave this page.")}
            </p>
            <p className="text-sm text-[#535862] leading-5">
              {t('resources.signup.booking_validation.loading_subtitle', 'It can take up to 2 minutes.')}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100">
          <XCloseIcon className="h-6 w-6 text-[#a4a7ae]" />
        </button>
        <div className="pt-8 pb-6 px-6">
          <Button className="w-full !bg-[#222] !text-white !border-2 !border-[rgba(255,255,255,0.12)]" size="lg" isLoading={true} isDisabled={true}>
            {t('resources.signup.booking_validation.validating', 'Checking...')}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  )
}

// Success Modal - Connection successful
interface SuccessModalProps extends BaseModalProps {
  systemName: string
  onContinue: () => void
}

export function BookingValidationSuccessModal({ isOpen, onClose, systemName, onContinue }: SuccessModalProps) {
  const { t } = useTranslation()
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="relative flex flex-col w-full">
        <div className="flex flex-col gap-4 items-start pt-6 px-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#dcfae6]">
            <CheckCircleIcon className="h-6 w-6 text-[#079455]" />
          </div>
          <div className="flex flex-col gap-0.5 w-full">
            <p className="text-xl font-semibold text-[#181d27] leading-[30px]">
              {t('resources.signup.booking_validation.success_title', 'Reservation system successfully connected')}
            </p>
            <p className="text-sm text-[#535862] leading-5">
              {t('resources.signup.booking_validation.success_message', '{{name}} is now connected with Bonnie.', { name: systemName })}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100">
          <XCloseIcon className="h-6 w-6 text-[#a4a7ae]" />
        </button>
        <div className="pt-8 pb-6 px-6">
          <Button className="w-full !bg-[#222] !text-white !border-2 !border-[rgba(255,255,255,0.12)] hover:!bg-[#333]" size="lg" onPress={onContinue}>
            {t('resources.signup.common.continue', 'Continue')}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  )
}

// Error Modal - Can retry
interface ErrorModalProps extends BaseModalProps {
  onTryAgain: () => void
  onDoLater: () => void
}

export function BookingValidationErrorModal({ isOpen, onClose, onTryAgain, onDoLater }: ErrorModalProps) {
  const { t } = useTranslation()
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="relative flex flex-col w-full">
        <div className="flex flex-col gap-4 items-start pt-6 px-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fef0c7]">
            <AlertCircleIcon className="h-6 w-6 text-[#dc6803]" />
          </div>
          <div className="flex flex-col gap-0.5 w-full">
            <p className="text-xl font-semibold text-[#181d27] leading-[30px]">
              {t('resources.signup.booking_validation.error_title', 'An error has occurred')}
            </p>
            <p className="text-sm text-[#535862] leading-5">
              {t('resources.signup.booking_validation.error_message', "We couldn't connect to your reservation system.")}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100">
          <XCloseIcon className="h-6 w-6 text-[#a4a7ae]" />
        </button>
        <div className="pt-8 pb-6 px-6 flex gap-3">
          <Button className="flex-1 !bg-white !text-[#414651] !border !border-[#d5d7da] hover:!bg-[#fafafa]" size="lg" onPress={onDoLater}>
            {t('resources.signup.booking_validation.do_later', "I'll do it later")}
          </Button>
          <Button className="flex-1 !bg-[#222] !text-white !border-2 !border-[rgba(255,255,255,0.12)] hover:!bg-[#333]" size="lg" onPress={onTryAgain}>
            {t('resources.signup.booking_validation.try_again', 'Try again')}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  )
}

// Error Modal - Contact support (multiple failures)
interface ContactSupportModalProps extends BaseModalProps {
  onDoLater: () => void
  onContactSupport: () => void
}

export function BookingValidationContactSupportModal({ isOpen, onClose, onDoLater, onContactSupport }: ContactSupportModalProps) {
  const { t } = useTranslation()
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="relative flex flex-col w-full">
        <div className="flex flex-col gap-4 items-start pt-6 px-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fef0c7]">
            <AlertCircleIcon className="h-6 w-6 text-[#dc6803]" />
          </div>
          <div className="flex flex-col gap-0.5 w-full">
            <p className="text-xl font-semibold text-[#181d27] leading-[30px]">
              {t('resources.signup.booking_validation.contact_support_title', "We can't connect with your reservation system")}
            </p>
            <p className="text-sm text-[#535862] leading-5">
              {t('resources.signup.booking_validation.contact_support_message', 'Our support team can help you set up your booking integration.')}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100">
          <XCloseIcon className="h-6 w-6 text-[#a4a7ae]" />
        </button>
        <div className="pt-8 pb-6 px-6 flex gap-3">
          <Button className="flex-1 !bg-white !text-[#414651] !border !border-[#d5d7da] hover:!bg-[#fafafa]" size="lg" onPress={onDoLater}>
            {t('resources.signup.booking_validation.do_later', "I'll do it later")}
          </Button>
          <Button className="flex-1 !bg-[#0d9488] !text-white !border-2 !border-[rgba(255,255,255,0.12)] hover:!bg-[#0f766e]" size="lg" onPress={onContactSupport}>
            {t('resources.signup.booking_validation.contact_support', 'Contact support')}
          </Button>
        </div>
      </div>
    </ModalWrapper>
  )
}

// Tutorial Modal - How to get credentials for each reservation system
interface TutorialModalProps extends BaseModalProps {
  bookingSystem: BookingSystem | null
  systemName: string
}

// Tutorial content for each reservation system
interface TutorialContent {
  title: string
  subtitle?: string
  steps: ReactNode[]
  connectUrl?: string
  connectLabel?: string
  image?: string
}

const TUTORIAL_CONTENT: Record<BookingSystem, TutorialContent> = {
  formitable: {
    title: 'Integrate with Formitable',
    subtitle: 'Follow the following steps to integrate with Formitable.',
    steps: [
      <>1. Login to Formitable, go to the team page, click on "+ Team member" and add <TutorialLink href="mailto:pollie-formitable@pollie.ai">pollie-formitable@pollie.ai</TutorialLink> as email</>,
      '2. Scroll to the bottom of the page and click "Get API Key"',
      '3. Click permissions and enable all',
      '4. Copy API key and paste it in the field',
      '4. Copy restaurant ID and paste in the field',
      "5. That's it, we're good to go!",
    ],
    connectUrl: 'https://app.formitable.com',
    connectLabel: 'Connect with Formitable',
  },
  zenchef: {
    title: 'How to connect Zenchef with Bonnie',
    steps: [
      <>1. Log in to your <TutorialLink href="https://app.zenchef.com">Zenchef dashboard</TutorialLink></>,
      <>2. Navigate to <TutorialLink href="https://app.zenchef.com/zw#/parameters/partners">Partners</TutorialLink> in settings</>,
      '3. Find and enable the Bonnie integration',
      '4. Copy your Restaurant ID',
    ],
  },
  thefork: {
    title: 'How to connect TheFork with Bonnie',
    steps: [
      '1. Our team will contact you to set up the integration',
      '2. We will enable the connection on our side',
      '3. No action needed from you at this moment',
    ],
  },
  opentable: {
    title: 'How to connect OpenTable with Bonnie',
    steps: [
      <>1. Log in to your <TutorialLink href="https://guestcenter.opentable.com">OpenTable Guest Center</TutorialLink></>,
      '2. Click on "Phone automation" in the left sidebar',
      '3. Find Bonnie in the available integrations',
      '4. Enter your verification code and confirm',
    ],
  },
  guestplan: {
    title: 'How to connect Guestplan with Bonnie',
    steps: [
      <>1. Log in to your <TutorialLink href="https://app.guestplan.com">Guestplan account</TutorialLink></>,
      <>2. Go to <TutorialLink href="https://app.guestplan.com/apps/manage">Apps {'>'} Manage Apps</TutorialLink></>,
      '3. Find "Bonnie" in the list of integrations',
      '4. Click "Install" to enable the Bonnie integration',
    ],
  },
  tablefever: {
    title: 'How to connect TableFever with Bonnie',
    steps: [
      '1. Our team will contact you to set up the integration',
      '2. We will enable the connection on our side',
      '3. No action needed from you at this moment',
    ],
  },
  gotable: {
    title: 'How to connect GoTable with Bonnie',
    steps: [
      <>1. Log in to your <TutorialLink href="https://gotable.app">GoTable dashboard</TutorialLink></>,
      '2. Go to Instellingen > Integraties (Settings > Integrations)',
      '3. Find the "Bonnie" section',
      '4. Check the box to give Bonnie access to add and manage reservations',
      '5. Click "Wijzig Bonnie instellingen" to save your settings',
    ],
  },
  sevenrooms: {
    title: 'How to connect SevenRooms with Bonnie',
    steps: [
      '1. Log in to your SevenRooms account',
      '2. Go to Settings > Integrations',
      '3. Enable the Bonnie integration',
      '4. Copy your API credentials',
    ],
  },
  resy: {
    title: 'How to connect Resy with Bonnie',
    steps: [
      '1. Log in to your Resy OS account',
      '2. Navigate to Integrations',
      '3. Enable Bonnie and copy your credentials',
    ],
  },
  other: {
    title: 'Connecting your reservation system',
    steps: [
      'Your reservation system is not directly integrated yet.',
      'Please contact support for assistance.',
    ],
  },
}

export function BookingTutorialModal({ isOpen, onClose, bookingSystem, systemName }: TutorialModalProps) {
  const content = bookingSystem ? TUTORIAL_CONTENT[bookingSystem] : null
  const hasConnectUrl = content?.connectUrl
  
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="relative flex flex-col w-full max-h-[80vh]">
        {/* Header */}
        <div className="flex flex-col items-start pt-6 px-6">
          {/* Help icon — only show for generic tutorials (no connect URL) */}
          {!hasConnectUrl && (
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#f4ebff]">
              <HelpCircleIcon className="h-6 w-6 text-[#6941c6]" />
            </div>
          )}
          {/* Title & subtitle */}
          <div className="flex flex-col gap-1 w-full">
            <p className="text-lg font-semibold text-[#181d27] leading-7">
              {content?.title || `How to connect ${systemName} with Bonnie`}
            </p>
            {content?.subtitle && (
              <p className="text-sm text-[#535862] leading-5">
                {content.subtitle}
              </p>
            )}
          </div>
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100"
        >
          <XCloseIcon className="h-6 w-6 text-[#a4a7ae]" />
        </button>
        
        {/* Tutorial content */}
        <div className="px-6 py-5 overflow-y-auto">
          {content ? (
            <div className="flex flex-col gap-3">
              {content.steps.map((step, index) => (
                <p key={index} className="text-sm text-[#535862] leading-5">
                  {step}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#535862] leading-5">
              Tutorial content coming soon for {systemName}.
            </p>
          )}
          
        </div>
        
        {/* Actions */}
        <div className="pt-2 pb-6 px-6">
          {hasConnectUrl ? (
            <a
              href={content.connectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#181d27] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#333] transition-colors"
            >
              {content.connectLabel || `Connect with ${systemName}`}
              <ArrowUpRightIcon className="h-5 w-5" />
            </a>
          ) : (
            <Button
              className="w-full !bg-[#222] !text-white !border-2 !border-[rgba(255,255,255,0.12)] hover:!bg-[#333]"
              size="lg"
              onPress={onClose}
            >
              Got it
            </Button>
          )}
        </div>
      </div>
    </ModalWrapper>
  )
}

// OpenTable Partner ID Modal - Shows generated partner ID and instructions
interface OpenTablePartnerIdModalProps extends BaseModalProps {
  partnerId: string
  isLoading?: boolean
  onContinue: () => void
}

export function OpenTablePartnerIdModal({ isOpen, onClose, partnerId, isLoading, onContinue }: OpenTablePartnerIdModalProps) {
  const [copied, setCopied] = useState(false)
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(partnerId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = partnerId
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="relative flex flex-col w-full">
        {/* Header */}
        <div className="flex flex-col gap-4 items-start pt-6 px-6">
          {/* OpenTable logo */}
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f5f5f5]">
            <img src="/images/opentableIcon.svg" alt="OpenTable" className="h-6 w-6" />
          </div>
          {/* Text */}
          <div className="flex flex-col gap-1 w-full">
            <p className="text-xl font-semibold text-[#181d27] leading-[30px]">
              Connect OpenTable with Bonnie
            </p>
            <p className="text-sm text-[#535862] leading-5">
              Use your Unique Identifier below to complete the integration in your OpenTable account.
            </p>
          </div>
        </div>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-100"
        >
          <XCloseIcon className="h-6 w-6 text-[#a4a7ae]" />
        </button>
        
        {/* Partner ID display */}
        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-[#333] mb-2">
            Your Unique Identifier
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-lg border border-[#e7eaeb] bg-[#fafafa] px-4 py-3">
              {isLoading ? (
                <div className="h-5 w-48 animate-pulse rounded bg-[#e7eaeb]" />
              ) : (
                <code className="text-sm font-mono text-[#181d27] break-all">{partnerId}</code>
              )}
            </div>
            <button
              type="button"
              onClick={handleCopy}
              disabled={isLoading || !partnerId}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg border border-[#d5d7da] bg-white text-[#535862] hover:bg-[#fafafa] disabled:opacity-50"
            >
              {copied ? (
                <CheckCircleIcon className="h-5 w-5 text-[#079455]" />
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-6 pb-4">
          <p className="text-sm font-medium text-[#333] mb-3">Steps to complete:</p>
          <div className="flex flex-col gap-2">
            <p className="text-sm text-[#535862] leading-5">
              1. Log in to your <TutorialLink href="https://guestcenter.opentable.com">OpenTable Guest Center</TutorialLink>
            </p>
            <p className="text-sm text-[#535862] leading-5">
              2. Click on "Phone automation" in the left sidebar
            </p>
            <p className="text-sm text-[#535862] leading-5">
              3. Find Bonnie in the available integrations
            </p>
            <p className="text-sm text-[#535862] leading-5">
              4. Enter your Unique Identifier and confirm
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="pt-4 pb-6 px-6 flex gap-3">
          <Button
            className="flex-1 !bg-white !text-[#414651] !border !border-[#d5d7da] hover:!bg-[#fafafa]"
            size="lg"
            onPress={onClose}
          >
            I'll do it later
          </Button>
          <Button
            className="flex-1 !bg-[#222] !text-white !border-2 !border-[rgba(255,255,255,0.12)] hover:!bg-[#333]"
            size="lg"
            onPress={onContinue}
            isDisabled={isLoading}
          >
            Done
          </Button>
        </div>
      </div>
    </ModalWrapper>
  )
}
